import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Bell, Mail, MessageSquare, Clock, CheckCircle, XCircle,
  RefreshCw, Send, Play, AlertTriangle, Zap, Settings, Power
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { HelpTooltip } from '@/components/help/HelpTooltip';

export default function NotificationDashboard() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [sending, setSending] = useState({ email: false, whatsapp: false });
  const [filter, setFilter] = useState({ channel: '', status: '' });
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, jobsRes, notifRes] = await Promise.all([
        axiosInstance.get('/notifications/stats?days=7'),
        axiosInstance.get('/notification-jobs'),
        axiosInstance.get('/notifications/recent?limit=50')
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setNotifications(notifRes.data);
    } catch (error) {
      toast.error('Failed to load notification data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setSending(prev => ({ ...prev, email: true }));
    try {
      await axiosInstance.post(`/notifications/test/email?email=${encodeURIComponent(testEmail)}`);
      toast.success('Test email sent!');
      setTestEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSending(prev => ({ ...prev, email: false }));
    }
  };

  const handleSendTestWhatsApp = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    setSending(prev => ({ ...prev, whatsapp: true }));
    try {
      await axiosInstance.post(`/notifications/test/whatsapp?phone=${encodeURIComponent(testPhone)}`);
      toast.success('Test WhatsApp sent!');
      setTestPhone('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test WhatsApp');
    } finally {
      setSending(prev => ({ ...prev, whatsapp: false }));
    }
  };

  const handleRetry = async (notificationId) => {
    try {
      await axiosInstance.post(`/notifications/${notificationId}/retry`);
      toast.success('Notification retry sent!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to retry notification');
    }
  };

  const handleToggleJob = async (jobId, currentEnabled) => {
    try {
      await axiosInstance.post(`/notification-jobs/${jobId}/toggle`);
      toast.success(`Job ${currentEnabled ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle job');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const getStatusBadge = (status) => {
    const variants = {
      sent: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      success: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
      skipped: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      pending: { color: 'bg-blue-100 text-blue-800', icon: Clock }
    };
    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        <Icon size={12} />
        {status}
      </Badge>
    );
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter.channel && n.channel !== filter.channel) return false;
    if (filter.status && n.status !== filter.status) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            Notification Dashboard
            <HelpTooltip
              tip="Monitor all automated notifications: emails, WhatsApp, and scheduled jobs"
              docId="notifications-overview"
            />
          </h1>
          <p className="text-slate-500">Monitor and manage system notifications</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total (7 days)</p>
                  <p className="text-2xl font-bold">{stats.total_notifications}</p>
                </div>
                <Bell className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Emails</p>
                  <p className="text-2xl font-bold text-green-600">{stats.email.sent}</p>
                  {stats.email.failed > 0 && (
                    <p className="text-xs text-red-500">{stats.email.failed} failed</p>
                  )}
                </div>
                <Mail className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">WhatsApp</p>
                  <p className="text-2xl font-bold text-green-600">{stats.whatsapp.sent}</p>
                  {stats.whatsapp.failed > 0 && (
                    <p className="text-xs text-red-500">{stats.whatsapp.failed} failed</p>
                  )}
                </div>
                <MessageSquare className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Jobs Run</p>
                  <p className="text-2xl font-bold text-green-600">{stats.jobs.success}</p>
                  {stats.jobs.failed > 0 && (
                    <p className="text-xs text-red-500">{stats.jobs.failed} failed</p>
                  )}
                </div>
                <Zap className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['overview', 'jobs', 'recent', 'test'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
              activeTab === tab
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'jobs' && (
              <HelpTooltip
                tip="Scheduled tasks that automatically send notifications at set times"
                docId="notifications-overview#jobs"
                className="ml-1"
              />
            )}
            {tab === 'recent' && (
              <HelpTooltip
                tip="Recent notification sends with status: sent, failed, or skipped"
                docId="notifications-overview#recent"
                className="ml-1"
              />
            )}
            {tab === 'test' && (
              <HelpTooltip
                tip="Send a test notification to verify the system is working"
                docId="notifications-overview#testing"
                className="ml-1"
              />
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Failed */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <XCircle size={20} />
                Recent Failures
              </CardTitle>
            </CardHeader>
            <CardContent>
              {notifications.filter(n => n.status === 'failed').length === 0 ? (
                <p className="text-slate-500 text-center py-4">No recent failures 🎉</p>
              ) : (
                <div className="space-y-2">
                  {notifications.filter(n => n.status === 'failed').slice(0, 5).map(n => (
                    <div key={n.id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                      <div className="flex items-center gap-2">
                        {n.channel === 'email' ? <Mail size={16} /> : <MessageSquare size={16} />}
                        <span className="text-sm truncate max-w-[200px]">{n.recipient}</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleRetry(n.id)}>
                        Retry
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings size={20} />
                Job Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {jobs.slice(0, 6).map(job => (
                  <div key={job.job_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div>
                      <p className="text-sm font-medium">{job.name}</p>
                      <p className="text-xs text-slate-500">{job.cron_ist} IST</p>
                    </div>
                    {job.last_execution ? (
                      getStatusBadge(job.last_execution.status)
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">Not run</Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <Card>
          <CardHeader>
            <CardTitle>Notification Jobs ({jobs.length})</CardTitle>
            <CardDescription>All scheduled notification jobs with their status and timing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">
                      <span className="flex items-center gap-1">
                        Enabled
                        <HelpTooltip
                          tip="Turn off a job to stop it from running. Turn on to resume."
                          docId="notifications-overview#enable-disable"
                        />
                      </span>
                    </th>
                    <th className="text-left p-3">Job</th>
                    <th className="text-left p-3">
                      <span className="flex items-center gap-1">
                        Schedule (IST)
                        <HelpTooltip
                          tip="All times shown in IST (Indian Standard Time). Jobs run automatically."
                          docId="notifications-overview#timing"
                        />
                      </span>
                    </th>
                    <th className="text-left p-3">
                      <span className="flex items-center gap-1">
                        Channel
                        <HelpTooltip
                          tip="Email for detailed info, WhatsApp for urgent alerts, Both for critical"
                          docId="notifications-overview#channels"
                        />
                      </span>
                    </th>
                    <th className="text-left p-3">Last Run</th>
                    <th className="text-left p-3">
                      <span className="flex items-center gap-1">
                        Status
                        <HelpTooltip
                          tip="Sent = delivered successfully. Failed = error occurred. Skipped = conditions not met."
                          docId="notifications-overview#status"
                        />
                      </span>
                    </th>
                    <th className="text-left p-3">Next Run</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <tr key={job.job_id} className={`border-b hover:bg-slate-50 ${!job.enabled ? 'opacity-50' : ''}`}>
                      <td className="p-3">
                        <Switch
                          checked={job.enabled !== false}
                          onCheckedChange={() => handleToggleJob(job.job_id, job.enabled !== false)}
                        />
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{job.name}</p>
                          <p className="text-xs text-slate-500">{job.description}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline">{job.cron_ist}</Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {job.channel === 'email' && <Mail size={14} className="text-blue-500" />}
                          {job.channel === 'whatsapp' && <MessageSquare size={14} className="text-green-500" />}
                          {job.channel === 'both' && (
                            <>
                              <Mail size={14} className="text-blue-500" />
                              <MessageSquare size={14} className="text-green-500" />
                            </>
                          )}
                          {job.channel === 'system' && <Settings size={14} className="text-slate-500" />}
                        </div>
                      </td>
                      <td className="p-3 text-xs">
                        {job.last_execution ? formatTime(job.last_execution.started_at) : 'Never'}
                      </td>
                      <td className="p-3">
                        {job.last_execution ? getStatusBadge(job.last_execution.status) : (
                          <Badge className="bg-slate-100 text-slate-600">Not run</Badge>
                        )}
                      </td>
                      <td className="p-3 text-xs">
                        {job.next_run ? formatTime(job.next_run) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Notifications Tab */}
      {activeTab === 'recent' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Recent Notifications</CardTitle>
                <CardDescription>Latest notification sends with status</CardDescription>
              </div>
              <div className="flex gap-2">
                <select
                  value={filter.channel}
                  onChange={(e) => setFilter(prev => ({ ...prev, channel: e.target.value }))}
                  className="text-sm border rounded px-2 py-1"
                >
                  <option value="">All Channels</option>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
                <select
                  value={filter.status}
                  onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                  className="text-sm border rounded px-2 py-1"
                >
                  <option value="">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="skipped">Skipped</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Channel</th>
                    <th className="text-left p-3">Recipient</th>
                    <th className="text-left p-3">Subject/Preview</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Sent At</th>
                    <th className="text-left p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotifications.map(n => (
                    <tr key={n.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        {n.channel === 'email' ? (
                          <Mail size={16} className="text-blue-500" />
                        ) : (
                          <MessageSquare size={16} className="text-green-500" />
                        )}
                      </td>
                      <td className="p-3 max-w-[150px] truncate">{n.recipient}</td>
                      <td className="p-3 max-w-[200px] truncate">{n.subject}</td>
                      <td className="p-3">{getStatusBadge(n.status)}</td>
                      <td className="p-3 text-xs">{formatTime(n.sent_at)}</td>
                      <td className="p-3">
                        {n.status === 'failed' && (
                          <Button size="sm" variant="outline" onClick={() => handleRetry(n.id)}>
                            <RefreshCw size={14} className="mr-1" />
                            Retry
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredNotifications.length === 0 && (
                <p className="text-center text-slate-500 py-8">No notifications found</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail size={20} className="text-blue-500" />
                Test Email
              </CardTitle>
              <CardDescription>Send a test email to verify configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
                <Button onClick={handleSendTestEmail} disabled={sending.email}>
                  {sending.email ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare size={20} className="text-green-500" />
                Test WhatsApp
              </CardTitle>
              <CardDescription>Send a test WhatsApp to verify configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  placeholder="Enter phone number (with country code)"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                />
                <Button onClick={handleSendTestWhatsApp} disabled={sending.whatsapp}>
                  {sending.whatsapp ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
